import {
  useFeatureAssignments,
  useAssignFeatureToWorkspaces,
  useAssignFeatureToAccounts,
} from '@gripp/shared-logic';
import {
  LoadingIndicator,
  SaveFormTop,
  TopModalBar,
  useGetDimensions,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import '../../shared/style/reactTabStyle.css';
import { FeatureAssignmentSearchInput } from './featureAssignmentSearchInput';
import { FeatureWorkspaceAssignmentList } from './featureWorkspaceAssignmentList';
import { FeatureAccountAssignmentList } from './featureAccountAssignmentList';

interface Props {
  featureId: string;
  title: string;
  onComplete: () => void;
  initialTab?: 'workspace' | 'account';
}

export const FeatureAssignmentForm = ({
  featureId,
  title,
  onComplete,
  initialTab,
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { dimensions, onLayout } = useGetDimensions();
  const height = dimensions?.height ?? 0;
  const listMaxHeight = height - 50;

  const getTabIndex = (tab?: 'workspace' | 'account') => {
    if (tab === 'account') return 1;
    return 0; // workspace
  };

  const [tabIndex, setTabIndex] = useState(getTabIndex(initialTab));

  useEffect(() => {
    if (initialTab) setTabIndex(getTabIndex(initialTab));
  }, [initialTab, featureId]);

  const [workspaceAssignments, setWorkspaceAssignments] = useState<string[]>([]);
  const [originalWorkspaceAssignments, setOriginalWorkspaceAssignments] = useState<
    string[]
  >([]);

  const [accountAssignments, setAccountAssignments] = useState<string[]>([]);
  const [originalAccountAssignments, setOriginalAccountAssignments] = useState<
    string[]
  >([]);

  const [keyword, setKeyword] = useState('');

  const { data: assignmentsData, isPending: isAssignmentsLoading } =
    useFeatureAssignments(featureId);

  useEffect(() => {
    const workspaces =
      assignmentsData?.feature?.findFeatureAssignments?.workspaces || [];
    const accounts =
      assignmentsData?.feature?.findFeatureAssignments?.accounts || [];

    setOriginalWorkspaceAssignments(workspaces);
    setWorkspaceAssignments(workspaces);
    setOriginalAccountAssignments(accounts);
    setAccountAssignments(accounts);
  }, [featureId, assignmentsData]);

  const { mutateAsync: assignToWorkspaces, isPending: isAssignWorkspacesLoading } =
    useAssignFeatureToWorkspaces();

  const { mutateAsync: assignToAccounts, isPending: isAssignAccountsLoading } =
    useAssignFeatureToAccounts();

  const handleSubmit = async () => {
    if (tabIndex === 0) {
      // workspace tab
      await assignToWorkspaces({
        input: {
          featureId,
          workspaceIds: workspaceAssignments,
        },
      } as any);
    } else {
      // accounts tab
      await assignToAccounts({
        input: {
          featureId,
          accountIds: accountAssignments,
        },
      } as any);
    }
    // Invalidate feature queries to refresh the list
    queryClient.invalidateQueries({ queryKey: ['feature'] });
    queryClient.invalidateQueries({ queryKey: ['feature', 'assignments', featureId] });
    onComplete();
  };

  const isWorkspacesDirty = () =>
    originalWorkspaceAssignments.length !== workspaceAssignments.length ||
    originalWorkspaceAssignments.some((w) => !workspaceAssignments.includes(w)) ||
    workspaceAssignments.some((w) => !originalWorkspaceAssignments.includes(w));

  const isAccountsDirty = () =>
    originalAccountAssignments.length !== accountAssignments.length ||
    originalAccountAssignments.some((u) => !accountAssignments.includes(u)) ||
    accountAssignments.some((u) => !originalAccountAssignments.includes(u));

  const isDirty =
    tabIndex === 0
      ? isWorkspacesDirty()
      : isAccountsDirty();

  const showLoading = isAssignmentsLoading || isAssignWorkspacesLoading || isAssignAccountsLoading;

  useEffect(() => {
    setKeyword('');
  }, [tabIndex]);

  return (
    <SaveFormTop
      onSubmit={handleSubmit}
      title={title}
      topBar={TopModalBar}
      onCancel={onComplete}
      isDirty={!showLoading && isDirty}
    >
      {showLoading ? (
        <LoadingIndicator flexContainer />
      ) : (
        <View style={styles.container}>
          <FeatureAssignmentSearchInput
            keyword={keyword}
            onChange={setKeyword}
            placeholder={
              tabIndex === 0
                ? 'features.assignment.workspaceSearchPlaceholder'
                : 'features.assignment.accountSearchPlaceholder'
            }
          />
          <View style={styles.assignmentContainer} onLayout={onLayout}>
            <Tabs selectedIndex={tabIndex} onSelect={(i) => setTabIndex(i)}>
              <TabList>
                <Tab>{t('features.assignment.workspaces')}</Tab>
                <Tab>{t('features.assignment.accounts')}</Tab>
              </TabList>
              <View style={styles.listContainer}>
                <TabPanel>
                  <FeatureWorkspaceAssignmentList
                    featureId={featureId}
                    maxHeight={listMaxHeight}
                    keyword={keyword}
                    workspaceAssignments={workspaceAssignments}
                    onWorkspaceAssignmentsChange={setWorkspaceAssignments}
                  />
                </TabPanel>
                <TabPanel>
                  <FeatureAccountAssignmentList
                    featureId={featureId}
                    maxHeight={listMaxHeight}
                    keyword={keyword}
                    accountAssignments={accountAssignments}
                    onAccountAssignmentsChange={setAccountAssignments}
                  />
                </TabPanel>
              </View>
            </Tabs>
          </View>
        </View>
      )}
    </SaveFormTop>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%',
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 20,
  },
  assignmentContainer: { flex: 1, marginTop: 20 },
  listContainer: {
    borderTopRightRadius: 12,
    borderBottomRightRadius: 12,
    borderBottomLeftRadius: 12,
    overflow: 'hidden',
  },
});
